import { useEffect, useMemo, useRef, useState } from 'react'
import { EnvelopeIcon } from '@heroicons/react/24/outline';
import { useLocation } from 'react-router-dom';

import HeroBanner from './HeroBanner';
import FilterPanel from './FilterPanel';

import { type Status } from '../lib/consts';
import { RentalList } from './RentalList';
import { RentalContext } from '../contexts/RentalContext';
import useStore from '../lib/store';
import useFilters from '../hooks/useFilters';
import useRentals from '../hooks/useRentals';
import usePortCookies from '../hooks/usePortCookies';
import MapView from './MapView';
import { useLocations } from '../hooks/useLocation';
import { AlertSignupModal } from './AlertSignupModal';
import { Button } from './ui/Button';
import { EvergreenLinks } from './EvergreenLinks';
import { minPostedAtForDateRange } from '../lib/dateRange';

const ALERT_BANNER_SEEN_KEY = 'email-alert-banner-seen';
const HOMEPAGE_FOOTER_LOCATION_LINKS = ['ubud', 'canggu', 'uluwatu', 'seminyak'];
const MAP_SCOPE_WAIT_TIMEOUT_MS = 3000;
const DEFAULT_MAP_ZOOM = 12;
const MAP_BOUNDS_FILTER_MIN_ZOOM = 11;

interface HomePageProps {
  suspendUrlSync?: boolean;
}

export default function HomePage({ suspendUrlSync = false }: HomePageProps) {
  const [status, setStatus] = useState<Status>('idle');
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [showAlertBanner] = useState(() => localStorage.getItem(ALERT_BANNER_SEEN_KEY) !== 'true');
  const [isDesktopMapLayout, setIsDesktopMapLayout] = useState(() =>
    window.matchMedia('(min-width: 768px)').matches
  );
  const [filters, keywords, setFilters] = useFilters(suspendUrlSync);

  const showLikedOnly = useStore((state) => state.showLikedOnly);
  const setShowLikedOnly = useStore((state) => state.setShowLikedOnly);
  const displayCurrency = useStore((state) => state.displayCurrency);
  const setDisplayCurrency = useStore((state) => state.setDisplayCurrency);
  const selectedLocation = useStore((state) => state.map.selectedLocation);
  const mapBounds = useStore((state) => state.map.bounds);
  const mapZoom = useStore((state) => state.map.zoom);
  const setMap = useStore((state) => state.setMap);
  const searchViewportRef = useRef<HTMLDivElement | null>(null);
  const routerLocation = useLocation();

  // Remove this code once we're happy that folks have ported their cookies.
  usePortCookies();
  const keywordTerms = keywords.split(/\s+/).filter(Boolean);
  const minPostedAt = useMemo(() => minPostedAtForDateRange(filters.dateRange), [filters.dateRange]);
  const { locations, loading: locationsLoading } = useLocations(filters, minPostedAt);
  const requestedLocation = selectedLocation || filters.location.trim();
  const hasRequestedLocation = Boolean(requestedLocation);
  const hasInitialMapViewport = useMemo(() => {
    const params = new URLSearchParams(routerLocation.search);
    return params.has('lat') || params.has('lng') || params.has('z');
  }, [routerLocation.search]);
  const initialMapZoom = useMemo(() => {
    const zoomParam = new URLSearchParams(routerLocation.search).get('z');
    if (zoomParam === null) return DEFAULT_MAP_ZOOM;

    const zoom = Number(zoomParam);
    return Number.isFinite(zoom) ? zoom : DEFAULT_MAP_ZOOM;
  }, [routerLocation.search]);
  const hasMapsApiKey = Boolean(import.meta.env.VITE_GOOGLE_MAPS_API_KEY);
  const shouldUseMapBounds = useMemo(() => {
    if (!hasMapsApiKey || hasRequestedLocation) return false;

    const zoom = mapZoom ?? initialMapZoom;
    return zoom >= MAP_BOUNDS_FILTER_MIN_ZOOM;
  }, [hasMapsApiKey, hasRequestedLocation, initialMapZoom, mapZoom]);
  const shouldAwaitMapScope = hasMapsApiKey && !showLikedOnly
    && ((hasRequestedLocation && locationsLoading) || (shouldUseMapBounds && (!mapBounds || locationsLoading)));
  const mapScopeWaitKey = `${requestedLocation || (hasInitialMapViewport ? routerLocation.search : 'default-map')}:${shouldUseMapBounds}:${filters.dateRange}:${filters.min_bedrooms}`;
  const [timedOutMapScopeWaitKey, setTimedOutMapScopeWaitKey] = useState<string | null>(null);
  const waitForMapScope = shouldAwaitMapScope && timedOutMapScopeWaitKey !== mapScopeWaitKey;
  const { listRentals, hasMore, loadingMore, loadMore, totalMatches, errorMessage } = useRentals(
    setStatus,
    filters,
    keywords,
    minPostedAt,
    shouldUseMapBounds,
    waitForMapScope,
  );
  const canUseServerTotal = (!filters.price_range || filters.price_range === 'Infinity') && keywords.trim().length === 0;

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 768px)');
    const updateDesktopMapLayout = () => setIsDesktopMapLayout(mediaQuery.matches);

    updateDesktopMapLayout();
    mediaQuery.addEventListener('change', updateDesktopMapLayout);
    return () => mediaQuery.removeEventListener('change', updateDesktopMapLayout);
  }, []);

  useEffect(() => {
    const searchViewport = searchViewportRef.current;
    if (!searchViewport) return;

    const handleSearchViewportWheel = (event: globalThis.WheelEvent) => {
      if (event.deltaY <= 0) return;
      if (!window.matchMedia('(min-width: 768px)').matches) return;

      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest('[data-map-viewport]')) return;

      const distanceToTop = searchViewport.getBoundingClientRect().top;
      if (distanceToTop <= 0) return;

      const pageScrollAmount = Math.min(event.deltaY, distanceToTop);
      event.preventDefault();
      window.scrollBy({ top: pageScrollAmount, behavior: 'auto' });

      const remainingScroll = event.deltaY - pageScrollAmount;
      const rentalScrollContainer = target?.closest<HTMLElement>('#rental-scroll-container');
      if (remainingScroll > 0 && rentalScrollContainer) {
        rentalScrollContainer.scrollBy({ top: remainingScroll, behavior: 'auto' });
      }
    };

    searchViewport.addEventListener('wheel', handleSearchViewportWheel, { capture: true, passive: false });
    return () => searchViewport.removeEventListener('wheel', handleSearchViewportWheel, { capture: true });
  }, []);

  useEffect(() => {
    if (!shouldAwaitMapScope) return;

    const timer = window.setTimeout(() => {
      setTimedOutMapScopeWaitKey(mapScopeWaitKey);
    }, MAP_SCOPE_WAIT_TIMEOUT_MS);

    return () => window.clearTimeout(timer);
  }, [mapScopeWaitKey, shouldAwaitMapScope]);

  useEffect(() => {
    if (showAlertBanner) {
      localStorage.setItem(ALERT_BANNER_SEEN_KEY, 'true');
    }
  }, [showAlertBanner]);

  const clearLocationFilter = () => {
    if (!filters.location.trim()) return;

    setFilters({ ...filters, location: '' });
  };

  const updateFilters = (nextFilters: typeof filters) => {
    if (nextFilters.location !== filters.location) {
      setMap({ selectedLocation: null });
    }
    setFilters(nextFilters);
  };

  return (
    <div className="flex flex-col bg-white p-6">
      <HeroBanner />
      <div ref={searchViewportRef} className="flex min-h-screen flex-col md:h-dvh md:min-h-0">
        <label className="mb-2 inline-flex shrink-0 self-start items-center text-sm text-gray-600">
          <input type="checkbox" checked={showLikedOnly} onChange={(e) => setShowLikedOnly(e.target.checked)} className="mr-2" />
          Show liked only
        </label>
        {!showLikedOnly && <FilterPanel filters={filters} setFilters={updateFilters} displayCurrency={displayCurrency} setDisplayCurrency={setDisplayCurrency} />}
        <div className="flex flex-1 flex-col md:min-h-0 md:flex-row">
          <div id="rental-scroll-container" className="order-2 flex-1 px-4 py-4 md:order-1 md:min-h-0 md:overflow-y-auto">
            {!showLikedOnly && showAlertBanner && (
              <div className="mb-4 flex flex-col gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-900">Want first dibs on new matches?</p>
                  <p className="text-sm text-gray-600">Save this search and get new listings by email.</p>
                </div>
                <Button
                  type="button"
                  onClick={() => setShowAlertModal(true)}
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap bg-orange-600 text-white shadow-sm hover:bg-orange-700 focus:ring-orange-300"
                >
                  <EnvelopeIcon className="h-4 w-4" />
                  Get email alerts
                </Button>
              </div>
            )}
            <RentalContext.Provider value={{
              displayCurrency,
              keywordTerms,
            }}>
              <RentalList
                rentals={listRentals}
                status={status}
                wide={showLikedOnly}
                onCreateAlert={!showLikedOnly ? () => setShowAlertModal(true) : undefined}
                showCreateAlertButton={!showAlertBanner}
                showLikedOnly={showLikedOnly}
                hasMore={!showLikedOnly && hasMore}
                loadingMore={loadingMore}
                onLoadMore={loadMore}
                totalMatches={canUseServerTotal ? totalMatches : null}
                isMapScoped={shouldUseMapBounds}
                selectedLocation={requestedLocation || null}
                errorMessage={errorMessage}
              />
            </RentalContext.Provider>
          </div>
          {showLikedOnly || (
            <div data-map-viewport className="order-1 h-[40svh] shrink-0 border-b border-gray-200 md:order-2 md:h-auto md:w-[500px] md:border-b-0 md:border-l">
              <MapView
                locations={locations}
                focusLocation={filters.location.trim() || null}
                useInitialBounds={shouldUseMapBounds}
                suspendUrlSync={suspendUrlSync || !isDesktopMapLayout}
                onClearLocation={clearLocationFilter}
              />
            </div>
          )}
        </div>
      </div>
      {showAlertModal && (
        <AlertSignupModal
          filters={filters}
          selectedLocation={selectedLocation}
          bounds={mapBounds}
          displayCurrency={displayCurrency}
          onClose={() => setShowAlertModal(false)}
        />
      )}
      <footer className="mx-auto mt-10 w-full max-w-6xl border-t border-gray-200 pt-6">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Popular Bali rental areas</h2>
        <EvergreenLinks slugs={HOMEPAGE_FOOTER_LOCATION_LINKS} />
      </footer>
    </div >
  );
}
